import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Form, Formik } from 'formik';
import { filter, find, forEach, get, indexOf, isEqual, keys, map, size, sortBy } from 'lodash-es';

import ButtonAction from '../Common/Blocks/ButtonAction';
import Group from './Blocks/Group';
import Partie from './Blocks/Partie';
import Question from './Blocks/Question';
import HandleFields from './Fields';
import ErrorCustom from './Fields/ErrorCustom';

import useReducer from '../../Utils/useReducer';
import reducer from './QuotationBlock.reducer';

import * as types from './QuotationBlock.actionTypes';

import { ActionButtonEnum } from './QuotationBlock.enum';
import { getCodeFirstError } from '../../Utils/error';
import { checkIsNull } from '../../Utils/utils';
import { getQuestionsTransform, initializeQuestionsFormik, initializeState } from './QuotationBlock.transform';
import { checkConditionsByValues, getValueCustom } from './QuotationBlock.utils';
import { validate } from './QuotationBlock.validation';

import './QuotationBlock.scss';

/**
 * quotationBlock Form
 */
const QuotationBlockForm = ({
  initialState,
  fieldChanged,
  formik: { errors, setFieldValue, values, isValid },
  showButtonAddPartieItem,
  answersEventEmitter,
}) => {
  //
  const [state, dispatch] = useReducer(reducer, initialState);

  // console.log('initialState', initialState);
  // console.log('state', state);
  // console.log('values:', values);
  // console.log('data:', data);
  // console.log('parties:', parties);
  // console.log('groups:', groups);
  // console.log('allQuestions:', allQuestions);
  // console.log('errors:', errors);
  // console.log('codeFirstError:', codeFirstError);
  // console.log('--------------');

  /**
   * handle external field changed
   */
  const handleExternalFieldChanged = () => {
    try {
      const questions = getQuestionsTransform({ data: get(state, 'data') });
      const question = find(questions, { code: get(fieldChanged, 'code') });
      const idPartie = get(question, 'idPartie');
      const idGroup = get(question, 'idGroup');
      const idArea = get(question, 'idArea');
      if (get(question, 'value') !== get(fieldChanged, 'value')) {
        dispatch({
          type: types.SET_VALUE_QUESTION,
          idPartie,
          idGroup,
          idArea,
          code: get(fieldChanged, 'code'),
          value: get(fieldChanged, 'value'),
          setFieldValue,
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`handleExternalFieldChanged ${e.stack}`);
    }
  };

  /**
   * handle show/hide questions
   */
  const handleShowHideQuestion = ({ questions }) => {
    try {
      const questionsWithConditions = filter(questions, questionItem => size(get(questionItem, 'conditions', [])) > 0);
      // loop questions With Conditions
      forEach(questionsWithConditions, questionItem => {
        const idPartie = get(questionItem, 'idPartie');
        const idGroup = get(questionItem, 'idGroup');
        const idArea = get(questionItem, 'idArea');
        const code = get(questionItem, 'code');
        const show = get(questionItem, 'show');
        const init = get(questionItem, 'init');
        const conditions = get(questionItem, 'conditions');

        // calculate result show
        const resultShow = checkConditionsByValues({ conditions, values });
        // set display question and reset value
        if (!isEqual(show, resultShow)) {
          dispatch({
            type: types.SET_VALUE_SHOW_QUESTION,
            idPartie,
            idGroup,
            idArea,
            code,
            value: getValueCustom(init),
            show: resultShow,
            setFieldValue,
          });
        }
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`handleShowHideQuestion: ${e.stack}`);
    }
  };

  /**
   * handle CodeFirstError Of Question
   */
  const handleCodeFirstError = ({ questions }) => {
    try {
      const questionsSorted = sortBy(questions, item => get(item, 'sequence'));
      const codeFirstError = getCodeFirstError({ questions: questionsSorted, errors });
      if (codeFirstError !== get(state, 'codeFirstError')) {
        dispatch({ type: types.SET_CODE_FIRST_ERROR, codeFirstError });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`handleCodeFirstError: ${e.stack}`);
    }
  };

  /**
   * get all valid questions
   */
  const getAnswers = ({ questions }) => {
    try {
      // active Questions
      const activeQuestions = filter(questions, questionsItem => get(questionsItem, 'show') === true);

      // get question item
      const getQuestionItem = questionsItem => ({
        path: get(questionsItem, 'path'),
        code: get(questionsItem, 'code'),
        value: values[get(questionsItem, 'code')],
      });

      // get area item
      const getAreaItem = areaItem => {
        // filter questionsArea
        const activeQuestionsItem = filter(get(areaItem, 'questions'), questionItem => {
          const activeQuestionsFinded = find(activeQuestions, { code: get(questionItem, 'code') });
          return get(activeQuestionsFinded, 'show', false);
        });
        // map questionsItem
        const questionsActive = map(activeQuestionsItem, questionsItem => getQuestionItem(questionsItem));
        return questionsActive;
      };

      // get group item
      const getGroupItem = groupItem => {
        // map areas
        const areas = map(get(groupItem, 'areas'), areaItem => getAreaItem(areaItem)).flat();
        return areas;
      };

      // get data item
      const getDataItem = dataItem => {
        // map groups
        const answers = map(get(dataItem, 'groups'), groupItem => getGroupItem(groupItem)).flat();

        const validQuestions = {
          partie: {
            ref: get(dataItem, 'partie.ref'),
          },
          answers,
        };

        return validQuestions;
      };

      //
      const result = map(get(state, 'data'), dataItem => getDataItem(dataItem));
      return result;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getAnswers: ${e.stack}`);
    }
    return [];
  };

  /**
￼
   * add Additional Control for Detect Errores
   */
  const addAdditionalControlDetectErrores = () =>
    setTimeout(() => {
      setFieldValue();
    }, 100);

  useEffect(
    // eslint-disable-next-line consistent-return
    () => {
      try {
        const questions = getQuestionsTransform({ data: get(state, 'data') });

        // handle show/hide question
        handleShowHideQuestion({ questions });

        // handle code first error
        handleCodeFirstError({ questions });

        // event emitter answers
        answersEventEmitter({
          isValid,
          nbErrors: size(errors),
          // answers: isValid ? getAnswers({ questions }) : undefined,
          answers: getAnswers({ questions }),
          // last code question edited
          lastCodeQuestionEdited: get(state, 'codeLastQuestionEdited'),
        });

        // add additional control on new fields (add AyantDroit)
        const timer = addAdditionalControlDetectErrores();
        return () => {
          clearTimeout(timer);
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`useEffect: ${e}`);
      }
    },
    //
    [state, errors]
  );

  useEffect(() => {
    handleExternalFieldChanged();
  }, [fieldChanged]);

  /**
   * check exist error
   * @param {*} param0
   */
  const checkExistError = ({ code }) => indexOf(keys(errors), code) !== -1;

  /**
   * check show error
   * @param {*} param0
   */
  const checkShowError = ({ code }) => get(state, 'codeFirstError') === code;

  /**
   * get question item
   * @param {*} param0
   */
  // eslint-disable-next-line react/prop-types
  const getQuestionItem = ({ questionItem, idPartie, idGroup, idArea }) => {
    try {
      // get Question informations
      const code = get(questionItem, 'code');
      const name = get(questionItem, 'name');
      const showQuestion = get(questionItem, 'show');
      const type = get(questionItem, 'type');
      const selection = get(questionItem, 'selection');
      const validation = get(questionItem, 'validation');
      const hidden = get(questionItem, 'hidden');
      const disabled = get(questionItem, 'disabled');

      return (
        <Question key={code} code={code} name={name} show={showQuestion} hidden={hidden}>
          <HandleFields
            code={code}
            type={type}
            selection={selection}
            disabled={disabled}
            validate={validate({ code, type, validation, values })}
            showError={checkShowError({ code })}
            existError={checkExistError({ code })}
            handleBlur={v =>
              dispatch({
                type: types.SET_VALUE_QUESTION,
                idPartie,
                idGroup,
                idArea,
                code,
                value: getValueCustom(v),
                setFieldValue,
              })
            }
          />
          {code === get(state, 'codeFirstError') && <ErrorCustom error={errors[get(state, 'codeFirstError')]} />}
        </Question>
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getQuestionItem ${e.stack}`);
    }
    return <span />;
  };

  /**
   * get area item
   * @param {*} param0
   */
  // eslint-disable-next-line react/prop-types
  const getAreaItem = ({ areaItem, idPartie, idGroup }) => {
    try {
      // get Area informations
      const idArea = get(areaItem, 'id');
      const questionsArea = get(areaItem, 'questions');
      return (
        <div className="area" key={idArea}>
          {map(questionsArea, questionItem => getQuestionItem({ questionItem, idPartie, idGroup, idArea }))}
        </div>
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(` ${e.stack}`);
    }
    return <span />;
  };

  /**
   * get group item
   * @param {*} groupItem
   * @param {*} index
   */
  // eslint-disable-next-line react/prop-types
  const getGroupItem = ({ groupItem, idPartie, index }) => {
    try {
      // get group informations
      const idGroup = get(groupItem, 'id');
      const titleGroup = get(groupItem, 'title');
      const showGroup = get(groupItem, 'show');
      return (
        <Group
          key={idGroup}
          index={index}
          id={idGroup}
          title={titleGroup}
          show={showGroup}
          setShowGroup={() => dispatch({ type: types.SET_SHOW_GROUP, idPartie, idGroup })}
        >
          {map(get(groupItem, 'areas'), areaItem => getAreaItem({ areaItem, idPartie, idGroup }))}
        </Group>
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getGroupItem ${e.stack}`);
    }
    return <span />;
  };

  /**
   * get partie item
   */
  // eslint-disable-next-line react/prop-types
  const getPartieItem = ({ partieItem }) => {
    try {
      // get partie informations
      const idPartie = get(partieItem, 'id');
      const titlePartie = get(partieItem, 'title');
      const showPartie = get(partieItem, 'show');
      const isSubscriber = get(partieItem, 'partie.isSubscriber');
      const disabled = get(partieItem, 'partie.disabled');
      const hidden = get(partieItem, 'partie.hidden');

      return (
        <Partie
          key={idPartie}
          id={idPartie}
          title={titlePartie}
          show={showPartie}
          isSubscriber={isSubscriber}
          disabled={disabled}
          hidden={hidden}
          removePartie={() => dispatch({ type: types.REMOVE_PARTIE, id: idPartie })}
          setShowPartie={() => dispatch({ type: types.SET_SHOW_PARTIE, id: idPartie })}
        >
          {map(get(partieItem, 'groups'), (groupItem, index) => getGroupItem({ groupItem, idPartie, index }))}
        </Partie>
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getPartieItem ${e.stack}`);
    }
    return <span />;
  };

  return (
    <div className="quotation-block-f3eb7ea5-216c-4b49-b58e-8e10c63b72d8">
      <Form>
        <>
          {map(get(state, 'data'), partieItem => getPartieItem({ partieItem }))}
          {showButtonAddPartieItem && (
            <ButtonAction typeAction={ActionButtonEnum.ADD} action={() => dispatch({ type: types.ADD_PARTIE })} />
          )}
        </>
      </Form>
    </div>
  );
};

QuotationBlockForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  initialState: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  formik: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
  fieldChanged: PropTypes.shape({
    code: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  }),
  showButtonAddPartieItem: PropTypes.bool.isRequired,
  answersEventEmitter: PropTypes.func.isRequired,
};
QuotationBlockForm.defaultProps = {
  fieldChanged: {},
};

/**
 * QuotationBlock
 */
const QuotationBlock = ({
  dataQuotationProp: { data, dataPartieItem: schemaPartie },
  fieldChanged,
  answersEventEmitter,
}) => {
  const initialState = initializeState({ data, schemaPartie });
  const showButtonAddPartieItem = !checkIsNull(get(schemaPartie, 'id'));

  return (
    <Formik
      initialValues={initializeQuestionsFormik({ data: get(initialState, 'data') })}
      // update the Form `initialValues` with new value
      enableReinitialize
      render={({ setFieldValue, values, errors, isValid }) => (
        <QuotationBlockForm
          initialState={initialState}
          fieldChanged={fieldChanged}
          formik={{ errors, setFieldValue, values, isValid }}
          showButtonAddPartieItem={showButtonAddPartieItem}
          answersEventEmitter={answersEventEmitter}
        />
      )}
    />
  );
};

QuotationBlock.propTypes = {
  dataQuotationProp: PropTypes.shape({
    data: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
    dataPartieItem: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  }),
  fieldChanged: PropTypes.shape({
    code: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  }),
  answersEventEmitter: PropTypes.func.isRequired,
};

QuotationBlock.defaultProps = {
  dataQuotationProp: {
    dataPartieItem: {},
  },
  fieldChanged: {},
};

export default QuotationBlock;
